import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { validateResumeFile } from '../../utils/fileUpload';
import apiClient from '../../services/api';

const JobApplicationForm = ({ open, onClose, job }) => {
  const { dispatch } = useData();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentCompany: '',
    currentPosition: '',
    experience: '',
    education: '',
    coverLetter: '',
    resume: null,
    expectedSalary: '',
    noticePeriod: '',
    source: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = validateResumeFile(file);
      if (!validation.valid) {
        setErrors(prev => ({
          ...prev,
          resume: validation.error
        }));
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      resume: file
    }));
    
    // Clear error when file is selected
    if (errors.resume) {
      setErrors(prev => ({
        ...prev,
        resume: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate email format if email is provided
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    if (!formData.resume) {
      setErrors({ submit: 'Please upload your resume/CV' });
      return;
    }

    setIsSubmitting(true);
    setErrors((prev) => ({ ...prev, submit: '' }));
    
    try {
      // Create the application object
      const applicationData = {
        jobId: job.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        currentCompany: formData.currentCompany,
        currentPosition: formData.currentPosition,
        experience: formData.experience,
        education: formData.education,
        expectedSalary: formData.expectedSalary,
        noticePeriod: formData.noticePeriod,
        source: formData.source,
        coverLetter: formData.coverLetter,
        status: 'pending'
      };
      
      // Send application with file to backend
      const result = await apiClient.addJobApplicationWithFile(applicationData, formData.resume);
      
      if (result && result.success) {
        // Save the application to the context
        dispatch({ type: 'ADD_JOB_APPLICATION_LOCAL', payload: result.data });
        
        setSubmitSuccess(true);
        setTimeout(() => {
          handleClose();
          setSubmitSuccess(false);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            currentCompany: '',
            currentPosition: '',
            experience: '',
            education: '',
            coverLetter: '',
            resume: null,
            expectedSalary: '',
            noticePeriod: '',
            source: ''
          });
          setErrors({});
        }, 3000);
      } else {
        throw new Error(result?.error || 'Failed to submit application');
      }
      
    } catch (error) {
      console.error('Error submitting application:', error);
      setErrors({ submit: error.message || 'Failed to submit application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        currentCompany: '',
        currentPosition: '',
        experience: '',
        education: '',
        coverLetter: '',
        resume: null,
        expectedSalary: '',
        noticePeriod: '',
        source: ''
      });
      setErrors({});
      setSubmitSuccess(false);
    }
  };

  if (!job) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      disableEscapeKeyDown={isSubmitting}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
          <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            Apply for {job.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={job.department} size="small" color="primary" variant="outlined" />
          </Box>
        </Box>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {isSubmitting && (
          <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
        )}

        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you for your application! We will review it and get back to you soon.
          </Alert>
        )}

        {isSubmitting && !submitSuccess && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Uploading your application and resume. Please wait and do not close this window.
          </Alert>
        )}

        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.submit}
          </Alert>
        )}

        <Box
          component="form"
          id="job-application-form"
          onSubmit={handleSubmit}
          sx={{ opacity: isSubmitting ? 0.7 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}
        >
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Personal Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>

            {/* Professional Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 2 }}>
                Professional Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Company"
                name="currentCompany"
                value={formData.currentCompany}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Position"
                name="currentPosition"
                value={formData.currentPosition}
                onChange={handleInputChange}
                error={!!errors.currentPosition}
                helperText={errors.currentPosition}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Years of Experience"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                error={!!errors.experience}
                helperText={errors.experience}
                placeholder="e.g., 5 years"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Education"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                error={!!errors.education}
                helperText={errors.education}
                placeholder="e.g., Bachelor's in Computer Science"
              />
            </Grid>

            {/* Application Details */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 2 }}>
                Application Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Salary (PKR)"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleInputChange}
                error={!!errors.expectedSalary}
                helperText={errors.expectedSalary}
                placeholder="e.g., PKR 50,000"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Notice Period"
                name="noticePeriod"
                value={formData.noticePeriod}
                onChange={handleInputChange}
                error={!!errors.noticePeriod}
                helperText={errors.noticePeriod}
                placeholder="e.g., 30 days"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>How did you hear about us?</InputLabel>
                <Select
                  name="source"
                  value={formData.source}
                  label="How did you hear about us?"
                  onChange={handleInputChange}
                >
                  <MenuItem value="">Select an option</MenuItem>
                  <MenuItem value="website">Company Website</MenuItem>
                  <MenuItem value="job-board">Job Board</MenuItem>
                  <MenuItem value="social-media">Social Media</MenuItem>
                  <MenuItem value="referral">Employee Referral</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Resume/CV along with documents in a single file
                </Typography>
                <TextField
                  fullWidth
                  name="resume"
                  type="file"
                  inputProps={{ accept: '.pdf,application/pdf' }}
                  onChange={handleFileChange}
                  error={!!errors.resume}
                  helperText={errors.resume || 'Accepted format: PDF only (max 50MB)'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& input[type="file"]': {
                        padding: '10.5px 14px',
                        '&::file-selector-button': {
                          marginRight: '12px',
                          padding: '6px 16px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'primary.dark'
                          }
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cover Letter"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                multiline
                rows={4}
                error={!!errors.coverLetter}
                helperText={errors.coverLetter || 'Please explain why you are interested in this position and how your experience aligns with the role.'}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting || submitSuccess}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="job-application-form"
          variant="contained"
          disabled={isSubmitting || submitSuccess}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ minWidth: 160 }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobApplicationForm; 